/* Bridges Clerk auth tokens into the Convex client.
   Ensures the user record exists in Convex on first sign-in. */

import { useEffect, useRef, useCallback } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useMutation, useConvex } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { identifyPostHogUser, resetPostHogUser } from "@/lib/posthog";

interface ConvexAuthSyncProps {
  onUserReady: () => void;
  onUserSignedOut: () => void;
}

export function ConvexAuthSync({
  onUserReady,
  onUserSignedOut,
}: ConvexAuthSyncProps) {
  const { getToken, isSignedIn, isLoaded, userId } = useAuth();
  const { user } = useUser();
  const convex = useConvex();
  const ensureUserExists = useMutation(api.auth.ensureUserExists);
  const authSetupRef = useRef(false);
  const userReadyRef = useRef(false);

  const handleUserReady = useCallback(() => {
    if (!userReadyRef.current) {
      userReadyRef.current = true;
      const email = user?.primaryEmailAddress?.emailAddress;
      if (userId) {
        setSentryUser(userId, email);
        identifyPostHogUser(userId, email);
      }
      onUserReady();
    }
  }, [onUserReady, userId, user]);

  const handleUserSignedOut = useCallback(() => {
    userReadyRef.current = false;
    clearSentryUser();
    resetPostHogUser();
    onUserSignedOut();
  }, [onUserSignedOut]);

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      if (authSetupRef.current) {
        convex.clearAuth();
        authSetupRef.current = false;
        handleUserSignedOut();
      }
      return;
    }

    if (authSetupRef.current) return;
    authSetupRef.current = true;

    convex.setAuth(async () => {
      if (!isSignedIn) return null;
      try {
        const token = await getToken({ template: "convex" });
        if (token) return token;
      } catch {
        /* fall through */
      }
      try {
        return (await getToken()) || null;
      } catch {
        return null;
      }
    });

    const setupUser = async () => {
      await new Promise((r) => setTimeout(r, 100));
      try {
        await ensureUserExists();
        handleUserReady();
      } catch {
        setTimeout(async () => {
          try {
            await ensureUserExists();
            handleUserReady();
          } catch {
            /* Do not call handleUserReady so feed queries are not run before user exists */
          }
        }, 1000);
      }
    };

    setupUser().catch(() => {
      /* Only call handleUserReady if setupUser resolved (handled inside) */
    });

    return () => {
      if (!isSignedIn) {
        convex.clearAuth();
        authSetupRef.current = false;
      }
    };
  }, [
    isSignedIn,
    isLoaded,
    getToken,
    convex,
    ensureUserExists,
    handleUserReady,
    handleUserSignedOut,
  ]);

  return null;
}
